import React, { useCallback } from "react";
import { Box, FormControlLabel, Switch, Tooltip, Typography } from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import ZoomOutIcon from "@material-ui/icons/ZoomOut";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import CropSquare from "@material-ui/icons/CropSquare";
import Note from "@material-ui/icons/Note";
import ZoomOutMapIcon from "@material-ui/icons/ZoomOutMap";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import { AnnotationType } from "store/features/annotations/interfaces";
import { useMaterialDetails, useMaterialSpecs } from "pages/MaterialSpecs/hooks";
import { useDispatch, useSelector } from "react-redux";
import { getAnnotationsPermissions } from "store/features/annotations/annotations.selectors";
import { usePermissions } from "hooks";
import {
    getAdMaterialSelectedPageById,
    getMaterialDetailsById,
    getMaterialSpecById,
    setSelectedPage,
} from "store/features/materialSpecs/specs";
import { PermissionWrapper } from "components";
import { useTranslation } from "react-i18next";
import CropFreeIcon from "@material-ui/icons/CropFree";
import { ToggleButton, ToggleButtonGroup, IconButton, Divider } from "./Controls.styled";
import GetAppIcon from "@material-ui/icons/GetApp";
import { useDownloadMaterials } from "hooks/api";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import { useAdMaterialVisual } from "hooks/api/useAdMaterialVisual";
import { getMaterialType } from "utils";
interface ControlsProps {
    disableToolbar?: boolean;
    annotationType: AnnotationType;
    isDrawingMode: boolean;
    showAnnotations: boolean;
    onReturn(): void;
    onZoomIn?(): void;
    onZoomOut?(): void;
    onResetTransform?(): void;
    onDrawSelectionHandler(type: AnnotationType): void;
    onSetShowAnnotations(): void;
}

export const Controls = ({
    disableToolbar = false,
    annotationType,
    isDrawingMode,
    showAnnotations,
    onReturn,
    onZoomIn,
    onZoomOut,
    onResetTransform,
    onDrawSelectionHandler,
    onSetShowAnnotations,
}: ControlsProps) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const {
        setTab,
        selectedMaterialSpecId,
        selectedMaterialId,
        switchVisual,
        isVisualVisible,
        type,
        setPreviewVisibility,
        isPrinted,
    } = useMaterialSpecs();
    const visualAsset = useAdMaterialVisual(isPrinted, selectedMaterialSpecId, selectedMaterialId);
    const materialSpec = useSelector(getMaterialSpecById(selectedMaterialSpecId));
    const allMaterials = materialSpec?.materials || [];
    const materialDetails = useSelector(getMaterialDetailsById(selectedMaterialSpecId, selectedMaterialId));
    const materialPages = useSelector(getAdMaterialSelectedPageById(selectedMaterialSpecId, selectedMaterialId));

    const { refetch: downloadMaterial, isFetching } = useDownloadMaterials("AD_MATERIAL", selectedMaterialId || 0);

    const { isMaterialEmpty } = useMaterialDetails(materialDetails);
    const permissions = useSelector(getAnnotationsPermissions);
    const materialDetailsPermissions = materialDetails ? [...materialDetails.permissions] : [];
    const { ANNOTATIONS, CREATE, SENDTOPRODUCTION, DOWNLOAD } = usePermissions([
        ...permissions,
        ...materialDetailsPermissions,
        ...(materialSpec?.permissions || []),
    ]);
    const isSelected = useCallback(
        (type: AnnotationType) => {
            return isDrawingMode && annotationType === type;
        },
        [isDrawingMode, annotationType]
    );

    const onDrawSelection = (type: AnnotationType) => {
        setTab("ANNOTATIONS");
        onDrawSelectionHandler(type);
    };

    const showAnnotationVisibilityIcon =
        (!isVisualVisible && ANNOTATIONS?.display) || (isVisualVisible && !SENDTOPRODUCTION?.disabled);

    const showDrawingAnnotationsIcons =
        (!isVisualVisible && CREATE?.display && ANNOTATIONS?.display) ||
        (isVisualVisible && !SENDTOPRODUCTION?.disabled);

    const isDownloadAvailable = DOWNLOAD?.display && !DOWNLOAD.disabled;

    const changeMaterial = (direction: "NEXT" | "PREVIOUS") => {
        if (materialDetails) {
            const currentMaterialIndex = allMaterials.findIndex((material) => materialDetails.id === material.id);
            // if first/last element is checked and disabled in the components calling this function
            if (currentMaterialIndex !== -1) {
                const newMaterialIndex = direction === "NEXT" ? currentMaterialIndex + 1 : currentMaterialIndex - 1;
                setPreviewVisibility(true, selectedMaterialSpecId, allMaterials[newMaterialIndex].id);
            }
        }
    };

    const showZoomingIcons = () => {
        if (isVisualVisible && visualAsset) {
            const visualType = getMaterialType(visualAsset.originalFile.format);

            return visualType === "IMAGE" || visualType === "PDF";
        }
        // is normal preview, not visual
        return type === "IMAGE" || type === "PDF";
    };

    return (
        <Box display="flex" p={1} pt={0.2}>
            <Box flex={2}>
                <IconButton disableRipple={true} onClick={onReturn}>
                    <ArrowBackIcon color="inherit" />
                </IconButton>
                {!isMaterialEmpty && (
                    <PermissionWrapper name="UPLOAD_ASSET" permissions={materialDetailsPermissions}>
                        {({ disabled }) => (
                            <FormControlLabel
                                label={
                                    <Typography variant="subtitle2" color="textSecondary">
                                        <Box fontWeight="fontWeightBold">{t("campaign.assets.label.switchVisual")}</Box>
                                    </Typography>
                                }
                                control={
                                    <Switch
                                        disabled={disabled}
                                        color="default"
                                        checked={isVisualVisible}
                                        onChange={(event, checked) => switchVisual(checked)}
                                    />
                                }
                            />
                        )}
                    </PermissionWrapper>
                )}
            </Box>
            {!disableToolbar && (
                <Box display="flex" flex={3} justifyContent="flex-end">
                    <Box display="flex" alignItems="center">
                        {showZoomingIcons() && (
                            <>
                                <Tooltip title={t("preview.zoomIn") || ""} interactive placement="top">
                                    <IconButton disableRipple onClick={onZoomIn}>
                                        <ZoomInIcon color="inherit" />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title={t("preview.zoomOut") || ""} interactive placement="top">
                                    <IconButton disableRipple onClick={onZoomOut}>
                                        <ZoomOutIcon color="inherit" />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title={t("preview.zoomToFit") || ""} interactive placement="top">
                                    <IconButton disableRipple onClick={onResetTransform}>
                                        <CropFreeIcon color="inherit" />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title={t("preview.resetSize") || ""} interactive placement="top">
                                    <IconButton disableRipple onClick={onResetTransform}>
                                        <ZoomOutMapIcon color="inherit" />
                                    </IconButton>
                                </Tooltip>

                                <Divider orientation="vertical" />
                            </>
                        )}

                        {/* for digitals: show if matSpec has at least 2 materials */}
                        {type !== "PDF" && allMaterials.length > 1 && (
                            <>
                                <Tooltip title={t("preview.previousCreative") || ""} interactive placement="top">
                                    <span>
                                        <IconButton
                                            disableRipple
                                            disabled={materialDetails?.id === allMaterials[0].id}
                                            onClick={() => changeMaterial("PREVIOUS")}>
                                            <NavigateBeforeIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>

                                <Tooltip title={t("preview.nextCreative") || ""} interactive placement="top">
                                    <span>
                                        <IconButton
                                            disableRipple
                                            disabled={materialDetails?.id === allMaterials[allMaterials.length - 1].id}
                                            onClick={() => changeMaterial("NEXT")}>
                                            <NavigateNextIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>

                                <Divider orientation="vertical" />
                            </>
                        )}

                        {!isVisualVisible && type === "PDF" && materialPages && materialPages.pages.length > 1 && (
                            <>
                                <Tooltip title={t("preview.previousPage") || ""} interactive placement="top">
                                    <span>
                                        <IconButton
                                            disableRipple
                                            disabled={materialPages.selectedPage <= 0}
                                            onClick={() =>
                                                dispatch(
                                                    setSelectedPage({
                                                        materialId: selectedMaterialId || 0,
                                                        direction: "PREVIOUS",
                                                    })
                                                )
                                            }>
                                            <KeyboardArrowUpIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>

                                <Tooltip title={t("preview.nextPage") || ""} interactive placement="top">
                                    <span>
                                        <IconButton
                                            disableRipple
                                            disabled={materialPages.selectedPage >= materialPages.pages.length - 1}
                                            onClick={() =>
                                                dispatch(
                                                    setSelectedPage({
                                                        materialId: selectedMaterialId || 0,
                                                        direction: "NEXT",
                                                    })
                                                )
                                            }>
                                            <KeyboardArrowDownIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>

                                <Divider orientation="vertical" />
                            </>
                        )}

                        {showAnnotationVisibilityIcon && (
                            <>
                                <Tooltip title={t("preview.showHideAnnotations") || ""} interactive placement="top">
                                    <IconButton disableRipple onClick={onSetShowAnnotations}>
                                        {showAnnotations ? (
                                            <VisibilityIcon color="inherit" />
                                        ) : (
                                            <VisibilityOffIcon color="inherit" />
                                        )}
                                    </IconButton>
                                </Tooltip>

                                {!showDrawingAnnotationsIcons && isDownloadAvailable && (
                                    <Divider orientation="vertical" />
                                )}
                            </>
                        )}
                        {showDrawingAnnotationsIcons && (
                            <>
                                <ToggleButtonGroup>
                                    <Tooltip title={t("preview.addSticker") || ""} interactive placement="top">
                                        <ToggleButton
                                            disableRipple
                                            disabled={CREATE?.disabled}
                                            value="STICKER"
                                            onClick={() => onDrawSelection("STICKER")}
                                            selected={isSelected("STICKER")}>
                                            <Note color="inherit" />
                                        </ToggleButton>
                                    </Tooltip>

                                    <Tooltip title={t("preview.addFrame") || ""} interactive placement="top">
                                        <ToggleButton
                                            disableRipple
                                            disabled={CREATE?.disabled}
                                            value="FRAME"
                                            onClick={() => onDrawSelection("FRAME")}
                                            selected={isSelected("FRAME")}>
                                            <CropSquare color="inherit" />
                                        </ToggleButton>
                                    </Tooltip>
                                </ToggleButtonGroup>

                                {isDownloadAvailable && <Divider orientation="vertical" />}
                            </>
                        )}
                        {isDownloadAvailable && (
                            <Tooltip title={t("preview.download") || ""} interactive placement="top">
                                <IconButton disableRipple onClick={() => downloadMaterial()} disabled={isFetching}>
                                    <GetAppIcon color="inherit" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
};
